// src/store/slices/themeSlice.js
import { createSlice } from '@reduxjs/toolkit';
import { tenantApi } from '../../services/tenantApi';

// Local storage keys
const THEME_STORAGE_KEY = 'kuppion_theme_v2'; // Versioned for security updates

// All possible theme cache keys for comprehensive cleanup
const ALL_THEME_CACHE_KEYS = [
  'kuppion_theme_cache_v3',
  'kuppion_theme_v2', 
  'kuppion_theme',
  'THEME_STORAGE_KEY'
];

// Sanitize data for localStorage - only store safe, non-sensitive information
const sanitizeThemeData = (data) => {
  if (!data || typeof data !== 'object') return null;
  
  return {
    primaryColor: typeof data.primaryColor === 'string' ? data.primaryColor : '',
    secondaryColor: typeof data.secondaryColor === 'string' ? data.secondaryColor : '',
    visualIdentity: data.visualIdentity ? {
      primaryColor: typeof data.visualIdentity.primaryColor === 'string' ? data.visualIdentity.primaryColor : '',
      secondaryColor: typeof data.visualIdentity.secondaryColor === 'string' ? data.visualIdentity.secondaryColor : '',
      logo: typeof data.visualIdentity.logo === 'string' ? data.visualIdentity.logo : '',
      name: typeof data.visualIdentity.name === 'string' ? data.visualIdentity.name : '',
      // Explicitly exclude email and other sensitive data
    } : null,
    lastUpdated: typeof data.lastUpdated === 'number' ? data.lastUpdated : Date.now(),
    tenantId: typeof data.tenantId === 'string' ? data.tenantId : null,
  };
};

// Load theme from localStorage with validation
const loadThemeFromStorage = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (!stored) return getDefaultThemeState();

    const parsed = JSON.parse(stored);
    const sanitized = sanitizeThemeData(parsed);
    
    if (!sanitized) return getDefaultThemeState();

    // Check age - if too old, use default
    const age = Date.now() - (sanitized.lastUpdated || 0);
    if (age > 24 * 60 * 60 * 1000) { // 24 hours
      localStorage.removeItem(THEME_STORAGE_KEY);
      return getDefaultThemeState();
    }

    return {
      primaryColor: sanitized.primaryColor || '#3A9355',
      secondaryColor: sanitized.secondaryColor || '#6B7280', 
      visualIdentity: sanitized.visualIdentity,
      lastUpdated: sanitized.lastUpdated,
      tenantId: sanitized.tenantId,
    };
  } catch (error) {
    console.warn('Failed to load theme from localStorage:', error);
    // Clear corrupted data
    try {
      localStorage.removeItem(THEME_STORAGE_KEY);
    } catch (e) {
      // Ignore
    }
    return getDefaultThemeState();
  }
};

// Get default theme state
const getDefaultThemeState = () => ({
  primaryColor: '#3A9355',
  secondaryColor: '#6B7280',
  visualIdentity: null,
  lastUpdated: null,
  tenantId: null,
});

// Save theme to localStorage with sanitization
const saveThemeToStorage = (state) => {
  try {
    const sanitized = sanitizeThemeData({
      primaryColor: state.primaryColor,
      secondaryColor: state.secondaryColor,
      visualIdentity: state.visualIdentity,
      lastUpdated: Date.now(),
      tenantId: state.tenantId,
    });
    
    if (sanitized) {
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(sanitized));
    }
  } catch (error) {
    console.warn('Failed to save theme to localStorage:', error);
  }
};

// Clear all theme data from all possible storage locations
const clearAllThemeData = () => {
  ALL_THEME_CACHE_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove theme cache ${key}:`, error);
    }
  });
  
  console.log('🎨 All theme cache cleared');
};

const initialState = loadThemeFromStorage();

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setVisualIdentity: (state, action) => {
      const payload = action.payload;
      
      // Validate and sanitize payload
      if (!payload || typeof payload !== 'object') return;
      
      const sanitizedPayload = sanitizeThemeData(payload);
      if (!sanitizedPayload) return;
      
      state.visualIdentity = {
        primaryColor: sanitizedPayload.primaryColor || state.primaryColor,
        secondaryColor: sanitizedPayload.secondaryColor || state.secondaryColor,
        logo: sanitizedPayload.visualIdentity?.logo || state.visualIdentity?.logo || '',
        name: sanitizedPayload.visualIdentity?.name || state.visualIdentity?.name || '',
        // Explicitly no email or sensitive data
      };
      
      state.primaryColor = sanitizedPayload.primaryColor || state.primaryColor;
      state.secondaryColor = sanitizedPayload.secondaryColor || state.secondaryColor;
      state.tenantId = sanitizedPayload.tenantId || state.tenantId;
      state.lastUpdated = Date.now();
      
      // Save to localStorage
      saveThemeToStorage(state);
    },
    
    updateColors: (state, action) => {
      const { primaryColor, secondaryColor } = action.payload || {};
      
      // Validate color format (hex colors only)
      const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
      
      if (primaryColor && hexColorRegex.test(primaryColor)) {
        state.primaryColor = primaryColor;
        if (state.visualIdentity) {
          state.visualIdentity.primaryColor = primaryColor;
        }
      }
      
      if (secondaryColor && hexColorRegex.test(secondaryColor)) {
        state.secondaryColor = secondaryColor;
        if (state.visualIdentity) {
          state.visualIdentity.secondaryColor = secondaryColor;
        }
      }
      
      state.lastUpdated = Date.now();
      saveThemeToStorage(state);
    },
    
    resetTheme: (state) => {
      const defaultState = getDefaultThemeState();
      
      state.primaryColor = defaultState.primaryColor;
      state.secondaryColor = defaultState.secondaryColor;
      state.visualIdentity = defaultState.visualIdentity;
      state.lastUpdated = defaultState.lastUpdated;
      state.tenantId = defaultState.tenantId;
      
      // Clear all theme storage
      clearAllThemeData();
    },
    
    clearThemeCache: (state) => {
      // Force refresh theme without clearing colors
      state.lastUpdated = null;
    },
    
    // New action for complete cleanup (used during logout)
    clearAllThemeData: (state) => {
      const defaultState = getDefaultThemeState();
      
      // Reset to default state
      Object.assign(state, defaultState);
      
      // Clear all possible storage locations
      clearAllThemeData();
      
      console.log('🎨 Theme state and cache completely cleared');
    },
    
    // Force tenant change (used when switching tenants)
    forceTenantChange: (state, action) => {
      const newTenantId = action.payload;
      
      // If tenant changed, clear everything except current colors (fallback)
      if (state.tenantId !== newTenantId) {
        console.log('🎨 Forcing tenant change from', state.tenantId, 'to', newTenantId);
        
        // Clear cache but keep current colors as fallback
        clearAllThemeData();
        
        // Reset visual identity but keep colors
        state.visualIdentity = null;
        state.lastUpdated = null;
        state.tenantId = newTenantId;
      }
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      tenantApi.endpoints.getTenantInfo.matchFulfilled,
      (state, { payload }) => {
        if (!payload?.value) return;
        
        const tenantData = payload.value;
        
        // More responsive updates - check for actual changes
        const shouldUpdate = !state.tenantId || 
                           state.tenantId !== tenantData.id ||
                           !state.lastUpdated ||
                           Date.now() - state.lastUpdated > 1 * 60 * 1000; // Reduced to 1 minute cache
        
        if (shouldUpdate) {
          const sanitizedData = {
            primaryColor: tenantData.primaryColor || tenantData.visualIdentity?.primaryColor || '#3A9355',
            secondaryColor: tenantData.secondaryColor || tenantData.visualIdentity?.secondaryColor || '#6B7280',
            visualIdentity: {
              primaryColor: tenantData.primaryColor || tenantData.visualIdentity?.primaryColor || '#3A9355',
              secondaryColor: tenantData.secondaryColor || tenantData.visualIdentity?.secondaryColor || '#6B7280',
              logo: tenantData.logo || tenantData.visualIdentity?.logo || '',
              name: tenantData.name || '',
              // Explicitly exclude email and other sensitive data from cache
            },
            tenantId: tenantData.id,
          };
          
          state.visualIdentity = sanitizedData.visualIdentity;
          state.primaryColor = sanitizedData.primaryColor;
          state.secondaryColor = sanitizedData.secondaryColor;
          state.tenantId = sanitizedData.tenantId;
          state.lastUpdated = Date.now();
          
          // Save to localStorage
          saveThemeToStorage(state);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Theme updated from tenant API (more responsive):', {
              primaryColor: state.primaryColor,
              secondaryColor: state.secondaryColor,
              tenantId: state.tenantId,
              hasLogo: !!state.visualIdentity?.logo,
              companyName: state.visualIdentity?.name,
           
            });
          }
        }
      }
    );
  },
});

// Actions
export const { 
  setVisualIdentity, 
  updateColors, 
  resetTheme, 
  clearThemeCache, 
  //clearAllThemeData,
  forceTenantChange 
} = themeSlice.actions;

// Selectors
export const selectPrimaryColor = (state) => state.theme?.primaryColor || '#3A9355';
export const selectSecondaryColor = (state) => state.theme?.secondaryColor || '#6B7280';

export const selectVisualIdentity = (state) => {
  const identity = state.theme?.visualIdentity;
  if (!identity || typeof identity !== 'object') return null;
  
  // Return sanitized visual identity (no sensitive data)
  return {
    primaryColor: identity.primaryColor || selectPrimaryColor(state),
    secondaryColor: identity.secondaryColor || selectSecondaryColor(state),
    logo: typeof identity.logo === 'string' ? identity.logo : '',
    name: typeof identity.name === 'string' ? identity.name : '',
    // No email or other sensitive data
  };
};

export const selectThemeState = (state) => ({
  primaryColor: selectPrimaryColor(state),
  secondaryColor: selectSecondaryColor(state),
  visualIdentity: selectVisualIdentity(state),
  lastUpdated: state.theme?.lastUpdated,
  tenantId: state.theme?.tenantId,
  isCacheValid: state.theme?.lastUpdated && 
                (Date.now() - state.theme.lastUpdated) < 1 * 60 * 1000, // 1 minute cache
});

// Export utility function for external use
export { clearAllThemeData };

// Reducer
export default themeSlice.reducer;